import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import EventPayForm from "./eventForm";
import { format } from "date-fns";

const EventDetails = () => {
  const navigate = useNavigate();
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [eventData, setEventData] = useState({});

  const handleOpenForm = (eventLink) => {
    if (eventLink.__component === "event.non-payment-link") {
      window.open(eventLink.LinkURL, "_blank"); // Opens in a new tab
    } else {
      setEventData(eventLink);
      setIsFormVisible(true);
    }
  };

  const handleCloseForm = () => {
    setEventData({});
    setIsFormVisible(false);
  };

  const handleContactUsClick = () => {
    navigate("/contact");
  };
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/events/${id}?populate[eventLink][populate]=*&populate[content_image]=*`,
          {
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_CMS_TOKEN}`,
            },
          }
        );
        const data = await response.json();
        setEvent(data.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching event data: ", error);
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  if (loading) {
    return (
      <div className="flex col-span-full h-[80vh] justify-center items-center text-custom-blue">
        <svg
          className="animate-spin h-5 w-5 mr-3 border-t-2 border-b-2 border-custom-orange rounded-full"
          viewBox="0 0 24 24"
        ></svg>
        Loading events...
      </div>
    );
  }

  if (!event) {
    return <div className="text-center mt-10">Event not found.</div>;
  }

  const { title, content, content_image } = event.attributes;
  const imageFormats = content_image?.data?.[0]?.attributes?.formats || {};

  const imageUrl =
    imageFormats.large?.url ||
    imageFormats.medium?.url ||
    imageFormats.small?.url ||
    content_image?.data?.[0]?.attributes?.url ||
    "";
  const contentLines = content.split("\n");

  const contentElements = [];
  contentLines.forEach((line) => {
    if (line.trim().startsWith("#")) {
      const hashes = line.match(/^#+/)[0];
      const headingLevel = hashes.length;
      const headingText = line.trim().replace(/^#+\s*/, "");
      contentElements.push({
        type: "heading",
        level: headingLevel,
        text: headingText,
      });
    } else if (line.trim().startsWith("- ")) {
      const bulletText = line
        .trim()
        .slice(2)
        .replace(/\*\*(.*?)\*\*/, "<span class='font-semibold'>$1</span>");
      contentElements.push({
        type: "bullet",
        text: bulletText,
        isHTML: true,
      });
    } else if (/\*\*(.*?)\*\*/.test(line)) {
      const boldText = line.replace(
        /\*\*(.*?)\*\*/,
        "<span class='font-semibold'>$1</span>"
      );
      contentElements.push({
        type: "paragraph",
        text: boldText,
        isHTML: true,
      });
    } else if (line.trim() !== "") {
      contentElements.push({
        type: "paragraph",
        text: line.trim(),
      });
    }
  });

  const headingStyles =
    "w-auto h-[34px] mb-[16px] font-primary text-[32px] font-semibold leading-tight text-left text-[#002A57]";
  const paragraphStyles =
    "text-[18px] font-secondary font-normal text-[#433E3F] text-justify";

  return (
    <>
      <div className="mb-10">
        <div className="relative w-auto h-[541px]">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[#0A0A0BB2]"></div>
          {/* Title */}
          <div className="absolute top-[137px] left-[120px] w-[651px] h-[335px] flex flex-col items-start pb-[102px]">
            <h1 className="text-[64px] font-primary font-bold leading-tight text-[#FAFAFA]">
              {title}
            </h1>

            {/* New Buttons Div */}
            <div className="flex mt-6 gap-4">
              {event.attributes.eventLink?.[0] && (
                <button
                  onClick={() =>
                    handleOpenForm(event.attributes.eventLink?.[0])
                  }
                  className="w-[152px] h-[48px] bg-custom-blue rounded-[30px] flex items-center justify-center"
                >
                  <span className="font-primary text-[13px] font-medium leading-[18px] text-white text-center">
                    {event.attributes.eventLink?.[0]?.__component ===
                    "event.non-payment-link"
                      ? "Register Now"
                      : "Buy Ticket"}
                  </span>
                </button>
              )}
              <div
                className="w-[152px] h-[48px] border border-white rounded-[30px] flex items-center justify-center cursor-pointer"
                onClick={handleContactUsClick}
              >
                <span className="font-primary text-[13px] font-medium leading-[18px] text-white text-center">
                  Contact Us
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row">
          {" "}
          <div className="w-full md:w-[808px]">
            <div className="relative top-[50px] right-[120px] left-[120px] h-auto flex flex-col gap-2 opacity-100 mb-16">
              <ul className="list-disc pl-5">
                {/* Adding list container for bullet points */}
                {contentElements.map((element, index) => {
                  if (element.type === "heading") {
                    const HeadingTag = `h${element.level}`;
                    return (
                      <HeadingTag key={index} className={headingStyles}>
                        {element.text}
                      </HeadingTag>
                    );
                  } else if (element.type === "bullet") {
                    return element.isHTML ? (
                      <li
                        key={index}
                        className={`${paragraphStyles} text-justify`}
                        dangerouslySetInnerHTML={{ __html: element.text }}
                      />
                    ) : (
                      <li
                        key={index}
                        className={`${paragraphStyles} text-justify`}
                      >
                        {element.text}
                      </li>
                    );
                  } else if (element.type === "paragraph") {
                    return element.isHTML ? (
                      <p
                        key={index}
                        className={paragraphStyles}
                        dangerouslySetInnerHTML={{ __html: element.text }}
                      />
                    ) : (
                      <p key={index} className={paragraphStyles}>
                        {element.text}
                      </p>
                    );
                  } else {
                    return null;
                  }
                })}
              </ul>
            </div>
          </div>
          <div className="w-full md:w-[362px] h-auto flex flex-col gap-6  ml-[239px]  mt-[50px]">
            {" "}
            {/* Date and Time Section */}
            <div className="w-full  bg-custom-blue rounded-[20px] p-[32px_24px] ">
              <div className="w-full flex items-center space-x-2 text-white text-left">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_2097_3585)">
                    <path
                      d="M21.375 2.99707H19.5V2.24707C19.5 1.84925 19.342 1.46771 19.0607 1.18641C18.7794 0.905106 18.3978 0.74707 18 0.74707C17.6022 0.74707 17.2206 0.905106 16.9393 1.18641C16.658 1.46771 16.5 1.84925 16.5 2.24707V2.99707H13.5V2.24707C13.5 1.84925 13.342 1.46771 13.0607 1.18641C12.7794 0.905106 12.3978 0.74707 12 0.74707C11.6022 0.74707 11.2206 0.905106 10.9393 1.18641C10.658 1.46771 10.5 1.84925 10.5 2.24707V2.99707H7.5V2.24707C7.5 1.84925 7.34196 1.46771 7.06066 1.18641C6.77936 0.905106 6.39782 0.74707 6 0.74707C5.60218 0.74707 5.22064 0.905106 4.93934 1.18641C4.65804 1.46771 4.5 1.84925 4.5 2.24707V2.99707H2.625C2.12772 2.99707 1.65081 3.19461 1.29917 3.54625C0.947544 3.89788 0.75 4.37479 0.75 4.87207V19.8721C0.75 20.3694 0.947544 20.8463 1.29917 21.1979C1.65081 21.5495 2.12772 21.7471 2.625 21.7471H13.125C13.2245 21.7471 13.3198 21.7076 13.3902 21.6372C13.4605 21.5669 13.5 21.4715 13.5 21.3721C13.5 21.2726 13.4605 21.1772 13.3902 21.1069C13.3198 21.0366 13.2245 20.9971 13.125 20.9971H2.625C2.32691 20.9962 2.04127 20.8774 1.83049 20.6666C1.6197 20.4558 1.50089 20.1702 1.5 19.8721V8.24707H22.5V14.6221C22.5 14.7215 22.5395 14.8169 22.6098 14.8872C22.6802 14.9576 22.7755 14.9971 22.875 14.9971C22.9745 14.9971 23.0698 14.9576 23.1402 14.8872C23.2105 14.8169 23.25 14.7215 23.25 14.6221V4.87207C23.25 4.37479 23.0525 3.89788 22.7008 3.54625C22.3492 3.19461 21.8723 2.99707 21.375 2.99707ZM17.25 2.24707C17.25 2.04816 17.329 1.85739 17.4697 1.71674C17.6103 1.57609 17.8011 1.49707 18 1.49707C18.1989 1.49707 18.3897 1.57609 18.5303 1.71674C18.671 1.85739 18.75 2.04816 18.75 2.24707V4.49707C18.75 4.69598 18.671 4.88675 18.5303 5.0274C18.3897 5.16805 18.1989 5.24707 18 5.24707C17.8011 5.24707 17.6103 5.16805 17.4697 5.0274C17.329 4.88675 17.25 4.69598 17.25 4.49707V2.24707ZM11.25 2.24707C11.25 2.04816 11.329 1.85739 11.4697 1.71674C11.6103 1.57609 11.8011 1.49707 12 1.49707C12.1989 1.49707 12.3897 1.57609 12.5303 1.71674C12.671 1.85739 12.75 2.04816 12.75 2.24707V4.49707C12.75 4.69598 12.671 4.88675 12.5303 5.0274C12.3897 5.16805 12.1989 5.24707 12 5.24707C11.8011 5.24707 11.6103 5.16805 11.4697 5.0274C11.329 4.88675 11.25 4.69598 11.25 4.49707V2.24707ZM5.25 2.24707C5.25 2.04816 5.32902 1.85739 5.46967 1.71674C5.61032 1.57609 5.80109 1.49707 6 1.49707C6.19891 1.49707 6.38968 1.57609 6.53033 1.71674C6.67098 1.85739 6.75 2.04816 6.75 2.24707V4.49707C6.75 4.69598 6.67098 4.88675 6.53033 5.0274C6.38968 5.16805 6.19891 5.24707 6 5.24707C5.80109 5.24707 5.61032 5.16805 5.46967 5.0274C5.32902 4.88675 5.25 4.69598 5.25 4.49707V2.24707ZM22.5 7.49707H1.5V4.87207C1.50089 4.57398 1.6197 4.28834 1.83049 4.07756C2.04127 3.86677 2.32691 3.74796 2.625 3.74707H4.5V4.49707C4.5 4.8949 4.65804 5.27643 4.93934 5.55773C5.22064 5.83904 5.60218 5.99707 6 5.99707C6.39782 5.99707 6.77936 5.83904 7.06066 5.55773C7.34196 5.27643 7.5 4.8949 7.5 4.49707V3.74707H10.5V4.49707C10.5 4.8949 10.658 5.27643 10.9393 5.55773C11.2206 5.83904 11.6022 5.99707 12 5.99707C12.3978 5.99707 12.7794 5.83904 13.0607 5.55773C13.342 5.27643 13.5 4.8949 13.5 4.49707V3.74707H16.5V4.49707C16.5 4.8949 16.658 5.27643 16.9393 5.55773C17.2206 5.83904 17.6022 5.99707 18 5.99707C18.3978 5.99707 18.7794 5.83904 19.0607 5.55773C19.342 5.27643 19.5 4.8949 19.5 4.49707V3.74707H21.375C21.6731 3.74796 21.9587 3.86677 22.1695 4.07756C22.3803 4.28834 22.4991 4.57398 22.5 4.87207V7.49707Z"
                      fill="white"
                    />
                    <path
                      d="M11.25 10.8721C11.25 10.6732 11.171 10.4824 11.0303 10.3417C10.8897 10.2011 10.6989 10.1221 10.5 10.1221H9C8.80109 10.1221 8.61032 10.2011 8.46967 10.3417C8.32902 10.4824 8.25 10.6732 8.25 10.8721V11.9971C8.25 12.196 8.32902 12.3867 8.46967 12.5274C8.61032 12.6681 8.80109 12.7471 9 12.7471H10.5C10.6989 12.7471 10.8897 12.6681 11.0303 12.5274C11.171 12.3867 11.25 12.196 11.25 11.9971V10.8721ZM9 11.9971V10.8721H10.5V11.9971H9Z"
                      fill="white"
                    />
                    <path
                      d="M6.75 10.8721C6.75 10.6732 6.67098 10.4824 6.53033 10.3417C6.38968 10.2011 6.19891 10.1221 6 10.1221H4.5C4.30109 10.1221 4.11032 10.2011 3.96967 10.3417C3.82902 10.4824 3.75 10.6732 3.75 10.8721V11.9971C3.75 12.196 3.82902 12.3867 3.96967 12.5274C4.11032 12.6681 4.30109 12.7471 4.5 12.7471H6C6.19891 12.7471 6.38968 12.6681 6.53033 12.5274C6.67098 12.3867 6.75 12.196 6.75 11.9971V10.8721ZM4.5 11.9971V10.8721H6V11.9971H4.5Z"
                      fill="white"
                    />
                    <path
                      d="M19.5 12.7471C19.6989 12.7471 19.8897 12.6681 20.0303 12.5274C20.171 12.3867 20.25 12.196 20.25 11.9971V10.8721C20.25 10.6732 20.171 10.4824 20.0303 10.3417C19.8897 10.2011 19.6989 10.1221 19.5 10.1221H18C17.8011 10.1221 17.6103 10.2011 17.4697 10.3417C17.329 10.4824 17.25 10.6732 17.25 10.8721V11.9971C17.25 12.196 17.329 12.3867 17.4697 12.5274C17.6103 12.6681 17.8011 12.7471 18 12.7471H19.5ZM18 10.8721H19.5V11.9971H18V10.8721Z"
                      fill="white"
                    />
                    <path
                      d="M11.25 14.2471C11.25 14.0482 11.171 13.8574 11.0303 13.7167C10.8897 13.5761 10.6989 13.4971 10.5 13.4971H9C8.80109 13.4971 8.61032 13.5761 8.46967 13.7167C8.32902 13.8574 8.25 14.0482 8.25 14.2471V15.3721C8.25 15.571 8.32902 15.7617 8.46967 15.9024C8.61032 16.0431 8.80109 16.1221 9 16.1221H10.5C10.6989 16.1221 10.8897 16.0431 11.0303 15.9024C11.171 15.7617 11.25 15.571 11.25 15.3721V14.2471ZM9 15.3721V14.2471H10.5V15.3721H9Z"
                      fill="white"
                    />
                    <path
                      d="M6.75 14.2471C6.75 14.0482 6.67098 13.8574 6.53033 13.7167C6.38968 13.5761 6.19891 13.4971 6 13.4971H4.5C4.30109 13.4971 4.11032 13.5761 3.96967 13.7167C3.82902 13.8574 3.75 14.0482 3.75 14.2471V15.3721C3.75 15.571 3.82902 15.7617 3.96967 15.9024C4.11032 16.0431 4.30109 16.1221 4.5 16.1221H6C6.19891 16.1221 6.38968 16.0431 6.53033 15.9024C6.67098 15.7617 6.75 15.571 6.75 15.3721V14.2471ZM4.5 15.3721V14.2471H6V15.3721H4.5Z"
                      fill="white"
                    />
                    <path
                      d="M10.5 16.8721H9C8.80109 16.8721 8.61032 16.9511 8.46967 17.0917C8.32902 17.2324 8.25 17.4232 8.25 17.6221V18.7471C8.25 18.946 8.32902 19.1367 8.46967 19.2774C8.61032 19.4181 8.80109 19.4971 9 19.4971H10.5C10.6989 19.4971 10.8897 19.4181 11.0303 19.2774C11.171 19.1367 11.25 18.946 11.25 18.7471V17.6221C11.25 17.4232 11.171 17.2324 11.0303 17.0917C10.8897 16.9511 10.6989 16.8721 10.5 16.8721ZM9 18.7471V17.6221H10.5V18.7471H9Z"
                      fill="white"
                    />
                    <path
                      d="M13.5 12.7471H15C15.1989 12.7471 15.3897 12.6681 15.5303 12.5274C15.671 12.3867 15.75 12.196 15.75 11.9971V10.8721C15.75 10.6732 15.671 10.4824 15.5303 10.3417C15.3897 10.2011 15.1989 10.1221 15 10.1221H13.5C13.3011 10.1221 13.1103 10.2011 12.9697 10.3417C12.829 10.4824 12.75 10.6732 12.75 10.8721V11.9971C12.75 12.196 12.829 12.3867 12.9697 12.5274C13.1103 12.6681 13.3011 12.7471 13.5 12.7471ZM13.5 10.8721H15V11.9971H13.5V10.8721Z"
                      fill="white"
                    />
                    <path
                      d="M12.75 15.3721C12.75 15.571 12.829 15.7617 12.9697 15.9024C13.1103 16.0431 13.3011 16.1221 13.5 16.1221C13.5995 16.1221 13.6948 16.0826 13.7652 16.0122C13.8355 15.9419 13.875 15.8465 13.875 15.7471C13.875 15.6476 13.8355 15.5522 13.7652 15.4819C13.6948 15.4116 13.5995 15.3721 13.5 15.3721V14.2471H15C15.0995 14.2471 15.1948 14.2076 15.2652 14.1372C15.3355 14.0669 15.375 13.9715 15.375 13.8721C15.375 13.7726 15.3355 13.6772 15.2652 13.6069C15.1948 13.5366 15.0995 13.4971 15 13.4971H13.5C13.3011 13.4971 13.1103 13.5761 12.9697 13.7167C12.829 13.8574 12.75 14.0482 12.75 14.2471V15.3721Z"
                      fill="white"
                    />
                    <path
                      d="M6 16.8721H4.5C4.30109 16.8721 4.11032 16.9511 3.96967 17.0917C3.82902 17.2324 3.75 17.4232 3.75 17.6221V18.7471C3.75 18.946 3.82902 19.1367 3.96967 19.2774C4.11032 19.4181 4.30109 19.4971 4.5 19.4971H6C6.19891 19.4971 6.38968 19.4181 6.53033 19.2774C6.67098 19.1367 6.75 18.946 6.75 18.7471V17.6221C6.75 17.4232 6.67098 17.2324 6.53033 17.0917C6.38968 16.9511 6.19891 16.8721 6 16.8721ZM4.5 18.7471V17.6221H6V18.7471H4.5Z"
                      fill="white"
                    />
                    <path
                      d="M18.375 13.4971C17.4108 13.4971 16.4683 13.783 15.6666 14.3187C14.8649 14.8543 14.2401 15.6157 13.8711 16.5065C13.5021 17.3973 13.4056 18.3775 13.5937 19.3231C13.7818 20.2688 14.2461 21.1374 14.9279 21.8192C15.6096 22.501 16.4783 22.9653 17.4239 23.1534C18.3696 23.3415 19.3498 23.245 20.2406 22.876C21.1314 22.507 21.8927 21.8822 22.4284 21.0805C22.9641 20.2788 23.25 19.3363 23.25 18.3721C23.2485 17.0796 22.7344 15.8405 21.8205 14.9266C20.9066 14.0127 19.6675 13.4986 18.375 13.4971ZM18.375 22.4971C17.5592 22.4971 16.7616 22.2551 16.0833 21.8019C15.4049 21.3486 14.8762 20.7044 14.564 19.9506C14.2518 19.1969 14.1701 18.3675 14.3293 17.5673C14.4884 16.7672 14.8813 16.0321 15.4582 15.4553C16.0351 14.8784 16.7701 14.4855 17.5703 14.3263C18.3704 14.1672 19.1998 14.2489 19.9536 14.5611C20.7073 14.8733 21.3516 15.402 21.8048 16.0803C22.2581 16.7587 22.5 17.5562 22.5 18.3721C22.4987 19.4657 22.0637 20.5142 21.2904 21.2875C20.5171 22.0608 19.4686 22.4958 18.375 22.4971Z"
                      fill="white"
                    />
                    <path
                      d="M20.542 16.7996L17.6252 19.7164L16.2996 18.3904C16.2289 18.322 16.1341 18.2842 16.0358 18.2851C15.9375 18.286 15.8434 18.3254 15.7739 18.3949C15.7044 18.4644 15.6649 18.5585 15.6641 18.6568C15.6632 18.7552 15.701 18.8499 15.7693 18.9206L17.3601 20.5117C17.4304 20.582 17.5258 20.6215 17.6252 20.6215C17.7246 20.6215 17.82 20.582 17.8903 20.5117L21.0722 17.3299C21.1405 17.2591 21.1783 17.1644 21.1775 17.0661C21.1766 16.9678 21.1372 16.8737 21.0676 16.8042C20.9981 16.7346 20.9041 16.6952 20.8057 16.6944C20.7074 16.6935 20.6127 16.7313 20.542 16.7996Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_2097_3585">
                      <rect
                        width="24"
                        height="24"
                        fill="white"
                        transform="translate(0 -0.00292969)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <span className="font-primary text-[16px] font-semibold leading-[21.86px]">
                  Date & Time
                </span>
              </div>

              <div className="w-[314px] border-t-[1px] border-neutrals-gray mt-4 opacity-50"></div>
              {/* Date & Time */}
              <div className="w-full mt-4 opacity-50 text-white">
                <span className="font-primary text-[14px] font-normal leading-[24.07px]">
                  {format(
                    new Date(`${event?.attributes.date}T00:00:00`),
                    "eee MMM dd yyyy"
                  )}
                  {" at "}
                  {format(
                    new Date(
                      `${event?.attributes.date}T${event?.attributes.time}`
                    ),
                    "h:mm a"
                  )}
                </span>
              </div>
            </div>
            {/* Location Section */}
            <div className="w-full bg-[#F2C94C] rounded-[20px] p-[32px_24px]">
              {/* Location Title */}
              <div className="w-full text-left flex items-center space-x-2">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_2097_3606)">
                    <path
                      d="M12.0013 4.79785C10.089 4.79785 8.5332 6.35363 8.5332 8.26599C8.5332 10.1784 10.089 11.7341 12.0013 11.7341C13.9136 11.7341 15.4694 10.1784 15.4694 8.26599C15.4694 6.35363 13.9136 4.79785 12.0013 4.79785ZM12.0013 10.778C10.6162 10.778 9.48936 9.65115 9.48936 8.26599C9.48936 6.88084 10.6162 5.75401 12.0013 5.75401C13.3864 5.75401 14.5132 6.88088 14.5132 8.26599C14.5132 9.6511 13.3864 10.778 12.0013 10.778Z"
                      fill="black"
                    />
                    <path
                      d="M12.0008 0.00390625C7.44483 0.00390625 3.73833 3.71041 3.73828 8.26638C3.73828 10.6532 5.0722 14.0039 7.70283 18.2251C9.64144 21.3359 11.6079 23.8001 11.6275 23.8246C11.7183 23.9379 11.8556 24.0039 12.0008 24.0039C12.1459 24.0039 12.2833 23.938 12.374 23.8246C12.3936 23.8001 14.3601 21.3359 16.2987 18.2251C18.9293 14.0038 20.2632 10.6532 20.2632 8.26638C20.2632 3.71041 16.5567 0.00390625 12.0008 0.00390625ZM12.0008 22.7465C10.4618 20.7348 4.69453 12.8868 4.69453 8.26638C4.69453 4.23766 7.97213 0.960109 12.0008 0.960109C16.0295 0.960109 19.3071 4.23766 19.3071 8.26638C19.3071 12.8868 13.5398 20.7348 12.0008 22.7465Z"
                      fill="black"
                    />
                    <path
                      d="M17.3872 4.24944C16.5449 3.12149 15.3447 2.27005 14.0076 1.85202C13.7553 1.77313 13.4874 1.91371 13.4086 2.16571C13.3299 2.41771 13.4703 2.68588 13.7223 2.76468C16.1394 3.5203 17.7633 5.73116 17.7633 8.26607C17.7633 8.53011 17.9774 8.74414 18.2414 8.74414C18.5054 8.74414 18.7195 8.53011 18.7195 8.26607C18.7195 6.80549 18.2588 5.41654 17.3872 4.24944Z"
                      fill="black"
                    />
                    <path
                      d="M12.4109 1.55925C12.2749 1.55105 12.1365 1.54688 11.9996 1.54688C11.7356 1.54688 11.5215 1.76091 11.5215 2.02495C11.5215 2.289 11.7356 2.50303 11.9996 2.50303C12.1173 2.50303 12.2363 2.50659 12.3531 2.51367C12.363 2.51428 12.3727 2.51456 12.3825 2.51456C12.6333 2.51456 12.8438 2.31909 12.8592 2.06531C12.8751 1.80178 12.6745 1.57523 12.4109 1.55925Z"
                      fill="black"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_2097_3606">
                      <rect
                        width="24"
                        height="24"
                        fill="white"
                        transform="translate(0 0.00390625)"
                      />
                    </clipPath>
                  </defs>
                </svg>

                <span className="font-primary text-[16px] font-semibold leading-[21.86px] text-black">
                  Location
                </span>
              </div>
              {/* Line */}
              <div className="w-[314px] border-t-[1px] border-[#B5B3B3] mt-4 opacity-50"></div>
              {/* Location */}
              <div className="w-full mt-4 text-black">
                <span className="font-primary text-[14px] font-normal leading-[24.07px]">
                  {event?.attributes.location || "Location not available"}
                </span>
              </div>
            </div>
            {/* Register Button */}
            {event.attributes.eventLink?.[0] && (
              <button
                onClick={() => handleOpenForm(event.attributes.eventLink?.[0])}
                className="w-[362px] h-[48px] bg-custom-blue rounded-[30px] flex items-center justify-center cursor-pointer"
              >
                <span className="font-primary text-[14px] font-medium leading-[18px] text-white text-center">
                  {event.attributes.eventLink?.[0]?.__component ===
                  "event.non-payment-link"
                    ? "Register Now"
                    : "Buy Ticket"}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
      {event.attributes.eventLink?.[0] && (
        <button
          onClick={() => handleOpenForm(event.attributes.eventLink?.[0])}
          className="relative w-[152px] h-[48px] mb-[100px] ml-[120px] p-0 bg-custom-blue rounded-[30px] flex items-center justify-center"
        >
          <span className="font-primary text-[13px] font-medium leading-[18px] text-white text-center">
            {event.attributes.eventLink?.[0]?.__component ===
            "event.non-payment-link"
              ? "Register Now"
              : "Buy Ticket"}
          </span>
        </button>
      )}

      <EventPayForm
        isVisible={isFormVisible}
        onClose={handleCloseForm}
        eventData={eventData}
      />
    </>
  );
};

export default EventDetails;
